import React from "react";
import { Box, Link, Typography } from "@mui/material";

const MARKDOWN_BOLD = /\*\*([^*]+)\*\*/g;
/** Single-asterisk emphasis; must not be part of **bold**. */
const MARKDOWN_ITALIC = /(?<!\*)\*([^*\n]+)\*(?!\*)/g;
const MARKDOWN_LINK = /\[([^\]]+)\]\(([^)]+)\)/g;
const BARE_URL = /(https?:\/\/[^\s)\]>]+)/g;
const MARKDOWN_HEADING = /^(#{1,3})\s+(.+)$/;
const MARKDOWN_BULLET = /^[-*]\s+(.+)$/;

/** Remove seoIndex and trailing ?/& from assistant URLs. */
export function cleanAssistantUrl(url: string): string {
  try {
    const parsed = new URL(url);
    parsed.searchParams.delete("seoIndex");
    let out = parsed.toString();
    if (out.endsWith("?")) {
      out = out.slice(0, -1);
    }
    return out;
  } catch {
    return url.replace(/([?&])seoIndex=1&?/g, "$1").replace(/[?&]$/, "");
  }
}

function linkTarget(href: string): "_self" | "_blank" {
  if (typeof window === "undefined") {
    return "_blank";
  }
  try {
    return new URL(href, window.location.origin).origin ===
      window.location.origin
      ? "_self"
      : "_blank";
  } catch {
    return "_blank";
  }
}

function renderLink(label: React.ReactNode, href: string, key: string) {
  const cleaned = cleanAssistantUrl(href);
  const target = linkTarget(cleaned);
  return (
    <Link
      key={key}
      href={cleaned}
      target={target}
      rel={target === "_blank" ? "noopener noreferrer" : undefined}
      underline="always"
      sx={{ color: "inherit", fontWeight: 500 }}
    >
      {label}
    </Link>
  );
}

/** Split plain text and auto-link bare https URLs. */
function renderTextWithBareUrls(
  text: string,
  keyPrefix: string
): React.ReactNode[] {
  const parts: React.ReactNode[] = [];
  let last = 0;
  let match: RegExpExecArray | null;
  const re = new RegExp(BARE_URL.source, BARE_URL.flags);
  let i = 0;
  while ((match = re.exec(text)) !== null) {
    if (match.index > last) {
      parts.push(text.slice(last, match.index));
    }
    const url = match[1];
    parts.push(renderLink(url, url, `${keyPrefix}-u-${i++}`));
    last = match.index + url.length;
  }
  if (last < text.length) {
    parts.push(text.slice(last));
  }
  return parts.length > 0 ? parts : [text];
}

type InlinePattern = {
  index: number;
  length: number;
  render: (key: string) => React.ReactNode;
};

function collectInlineMatches(
  text: string,
  pattern: RegExp,
  render: (inner: string, key: string) => React.ReactNode
): InlinePattern[] {
  const matches: InlinePattern[] = [];
  const re = new RegExp(pattern.source, pattern.flags);
  let match: RegExpExecArray | null;
  while ((match = re.exec(text)) !== null) {
    const inner = match[1];
    const index = match.index;
    const length = match[0].length;
    matches.push({
      index,
      length,
      render: (key) => render(inner, key)
    });
  }
  return matches;
}

function collectMarkdownLinkMatches(text: string): InlinePattern[] {
  const matches: InlinePattern[] = [];
  const re = new RegExp(MARKDOWN_LINK.source, MARKDOWN_LINK.flags);
  let match: RegExpExecArray | null;
  while ((match = re.exec(text)) !== null) {
    const label = match[1];
    const href = match[2];
    const index = match.index;
    const length = match[0].length;
    matches.push({
      index,
      length,
      render: (key) =>
        renderLink(renderInlineFormatting(label, `${key}-lbl`), href, key)
    });
  }
  return matches;
}

/** Bold, italic, markdown links, then bare URLs — applied left-to-right without overlapping spans. */
function renderInlineFormatting(
  text: string,
  keyPrefix: string
): React.ReactNode[] {
  const candidates: InlinePattern[] = [
    ...collectMarkdownLinkMatches(text),
    ...collectInlineMatches(text, MARKDOWN_BOLD, (inner, key) => (
      <Box component="span" key={key} sx={{ fontWeight: 600 }}>
        {renderInlineFormatting(inner, `${key}-b`)}
      </Box>
    )),
    ...collectInlineMatches(text, MARKDOWN_ITALIC, (inner, key) => (
      <Box component="span" key={key} sx={{ fontStyle: "italic" }}>
        {renderInlineFormatting(inner, `${key}-i`)}
      </Box>
    ))
  ].sort((a, b) => a.index - b.index);

  const parts: React.ReactNode[] = [];
  let last = 0;
  let i = 0;
  for (const hit of candidates) {
    if (hit.index < last) {
      continue;
    }
    if (hit.index > last) {
      parts.push(
        ...renderTextWithBareUrls(
          text.slice(last, hit.index),
          `${keyPrefix}-t-${i}`
        )
      );
    }
    parts.push(hit.render(`${keyPrefix}-m-${i++}`));
    last = hit.index + hit.length;
  }
  if (last < text.length) {
    parts.push(
      ...renderTextWithBareUrls(text.slice(last), `${keyPrefix}-tail`)
    );
  }
  return parts.length > 0 ? parts : renderTextWithBareUrls(text, keyPrefix);
}

function renderInlineParagraph(text: string, key: string) {
  if (!text) {
    return null;
  }
  return (
    <Typography
      key={key}
      variant="body2"
      component="div"
      sx={{ whiteSpace: "pre-wrap" }}
    >
      {renderInlineFormatting(text, key)}
    </Typography>
  );
}

function renderBlocks(content: string): React.ReactNode[] {
  const lines = content.split("\n");
  const blocks: React.ReactNode[] = [];
  let bulletBuffer: string[] = [];
  let blockIndex = 0;

  const flushBullets = () => {
    if (bulletBuffer.length === 0) {
      return;
    }
    blocks.push(
      <Box
        key={`ul-${blockIndex++}`}
        component="ul"
        sx={{ m: 0, pl: 2.5, mb: 0.5 }}
      >
        {bulletBuffer.map((item, idx) => (
          <Box component="li" key={`li-${idx}`} sx={{ mb: 0.25 }}>
            <Typography variant="body2" component="span">
              {renderInlineFormatting(item, `li-${blockIndex}-${idx}`)}
            </Typography>
          </Box>
        ))}
      </Box>
    );
    bulletBuffer = [];
  };

  for (let lineIndex = 0; lineIndex < lines.length; lineIndex++) {
    const line = lines[lineIndex];
    const trimmed = line.trim();

    if (trimmed === "") {
      flushBullets();
      continue;
    }

    const headingMatch = trimmed.match(MARKDOWN_HEADING);
    if (headingMatch) {
      flushBullets();
      const level = headingMatch[1].length;
      const title = headingMatch[2];
      blocks.push(
        <Typography
          key={`h-${blockIndex++}`}
          variant={level <= 2 ? "subtitle2" : "body2"}
          component="div"
          sx={{
            fontWeight: 600,
            mt: blockIndex > 1 ? 1 : 0,
            mb: 0.25
          }}
        >
          {renderInlineFormatting(title, `h-${blockIndex}`)}
        </Typography>
      );
      continue;
    }

    const bulletMatch = trimmed.match(MARKDOWN_BULLET);
    if (bulletMatch) {
      bulletBuffer.push(bulletMatch[1]);
      continue;
    }

    flushBullets();
    blocks.push(renderInlineParagraph(line, `p-${blockIndex++}`));
  }

  flushBullets();
  return blocks;
}

/**
 * Renders assistant markdown: headings, bullet lists, **bold**, *italic*, [label](url) links, bare URLs.
 */
const TetaMessageContent: React.FC<{ content: string }> = ({ content }) => {
  const blocks = renderBlocks(content);
  if (blocks.length === 0) {
    return (
      <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
        {content}
      </Typography>
    );
  }
  return <Box sx={{ display: "flex", flexDirection: "column" }}>{blocks}</Box>;
};

export default TetaMessageContent;
